import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, Building2, FileText, Shield, Save, AlertCircle,
  CheckCircle2, XCircle, Settings, Upload, ImageIcon, Trash2
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import type { BillingSettings as BillingSettingsType, TaxRate } from "@/types/billing";

export default function BillingSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<Partial<BillingSettingsType>>({
    country_code: 'ES',
    tax_id_type: 'NIF',
    verifactu_enabled: false,
    verifactu_environment: 'test',
    verifactu_software_name: 'Athenea MES',
    verifactu_software_version: '1.0.0',
    default_payment_terms_days: 30,
  });

  // Fetch existing settings
  const { data: existingSettings, isLoading } = useQuery<BillingSettingsType | null>({
    queryKey: ["billing-settings"],
    queryFn: async () => {
      const { data, error } = await api
        .from("billing_settings")
        .select("*")
        .limit(1)
        .maybeSingleRow();
      if (error) throw error;
      return data;
    },
  });

  // Load existing settings
  useEffect(() => {
    if (existingSettings) {
      setSettings(existingSettings);
    }
  }, [existingSettings]);

  // Fetch tax rates
  const { data: taxRates = [] } = useQuery<TaxRate[]>({
    queryKey: ["tax-rates"],
    queryFn: async () => {
      const { data, error } = await api
        .from("tax_rates")
        .select("*")
        .order("rate", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!settings.company_name || !settings.tax_id) {
        throw new Error("El nombre de empresa y NIF son obligatorios");
      }

      const data = {
        ...settings,
        updated_at: new Date().toISOString(),
        updated_by: user?.id,
      };

      if (settings.id) {
        const { error } = await api
          .from("billing_settings")
          .update(data)
          .eq("id", settings.id);
        if (error) throw error;
      } else {
        const { error } = await api
          .from("billing_settings")
          .insert(data);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-settings"] });
      toast.success("Configuración guardada");
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Cargando configuración...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/billing"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Configuración de Facturación</h1>
              <p className="text-muted-foreground">Datos de empresa y VeriFactu</p>
            </div>
          </div>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? "Guardando..." : "Guardar"}
          </Button>
        </div>

        <Tabs defaultValue="company" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="company">
              <Building2 className="w-4 h-4 mr-2" />
              Empresa
            </TabsTrigger>
            <TabsTrigger value="invoicing">
              <FileText className="w-4 h-4 mr-2" />
              Facturación
            </TabsTrigger>
            <TabsTrigger value="verifactu">
              <Shield className="w-4 h-4 mr-2" />
              VeriFactu
            </TabsTrigger>
          </TabsList>

          {/* Company Data */}
          <TabsContent value="company" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Datos Fiscales</CardTitle>
                <CardDescription>
                  Información que aparecerá en las facturas como emisor
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Razón Social *</Label>
                    <Input
                      value={settings.company_name || ''}
                      onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                      placeholder="Nombre legal de la empresa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre Comercial</Label>
                    <Input
                      value={settings.trade_name || ''}
                      onChange={(e) => setSettings({ ...settings, trade_name: e.target.value })}
                      placeholder="Nombre comercial (opcional)"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo documento</Label>
                    <Select
                      value={settings.tax_id_type}
                      onValueChange={(v) => setSettings({ ...settings, tax_id_type: v as any })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NIF">NIF</SelectItem>
                        <SelectItem value="CIF">CIF</SelectItem>
                        <SelectItem value="NIE">NIE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>NIF/CIF *</Label>
                    <Input
                      value={settings.tax_id || ''}
                      onChange={(e) => setSettings({ ...settings, tax_id: e.target.value.toUpperCase() })}
                      placeholder="B12345678"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Dirección</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Input
                    value={settings.address_line1 || ''}
                    onChange={(e) => setSettings({ ...settings, address_line1: e.target.value })}
                    placeholder="Calle, número, piso..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dirección (línea 2)</Label>
                  <Input
                    value={settings.address_line2 || ''}
                    onChange={(e) => setSettings({ ...settings, address_line2: e.target.value })}
                    placeholder="Polígono, edificio..."
                  />
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>C.P.</Label>
                    <Input
                      value={settings.postal_code || ''}
                      onChange={(e) => setSettings({ ...settings, postal_code: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ciudad</Label>
                    <Input
                      value={settings.city || ''}
                      onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Input
                      value={settings.province || ''}
                      onChange={(e) => setSettings({ ...settings, province: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>País</Label>
                    <Select
                      value={settings.country_code}
                      onValueChange={(v) => setSettings({ ...settings, country_code: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ES">España</SelectItem>
                        <SelectItem value="PT">Portugal</SelectItem>
                        <SelectItem value="FR">Francia</SelectItem>
                        <SelectItem value="DE">Alemania</SelectItem>
                        <SelectItem value="IT">Italia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
<Card>              <CardHeader>                <CardTitle>Logo de Empresa</CardTitle>                <CardDescription>                  Este logo aparecerá en las facturas y documentos                </CardDescription>              </CardHeader>              <CardContent className="space-y-4">                <div className="flex items-start gap-6">                  <div className="w-48 h-32 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden">                    {settings.logo_url ? (                      <img src={settings.logo_url} alt="Logo empresa" className="max-w-full max-h-full object-contain" />                    ) : (                      <div className="text-center text-muted-foreground">                        <ImageIcon className="w-8 h-8 mx-auto mb-2" />                        <p className="text-xs">Sin logo</p>                      </div>                    )}                  </div>                  <div className="flex-1 space-y-3">                    <div className="space-y-2">                      <Label>Subir logo</Label>                      <div className="flex gap-2">                        <Input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; if (file.size > 2 * 1024 * 1024) { toast.error("El archivo no puede superar 2MB"); return; } const formData = new FormData(); formData.append("file", file); formData.append("folder", "logos"); try { const res = await fetch("/upload", { method: "POST", body: formData }); const data = await res.json(); if (data.url) { 
                          const newSettings = { ...settings, logo_url: data.url };
                          setSettings(newSettings);
                          // Auto-guardar el logo
                          if (settings.id) {
                            await api.from("billing_settings").update({ logo_url: data.url, updated_at: new Date().toISOString() }).eq("id", settings.id);
                            queryClient.invalidateQueries({ queryKey: ["billing-settings"] });
                          }
                          toast.success("Logo guardado"); 
                        } } catch (err) { toast.error("Error al subir"); } }} className="flex-1" />                        {settings.logo_url && (<Button variant="outline" size="icon" onClick={() => setSettings({ ...settings, logo_url: undefined })}><Trash2 className="w-4 h-4 text-destructive" /></Button>)}                      </div>                      <p className="text-xs text-muted-foreground">PNG, JPG o SVG. Máximo 2MB. Recomendado: 400x200px</p>                    </div>                  </div>                </div>              </CardContent>            </Card>
                <CardTitle>Contacto</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input
                      value={settings.phone || ''}
                      onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={settings.email || ''}
                      onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Web</Label>
                    <Input
                      value={settings.website || ''}
                      onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Account */}
            <Card>
              <CardHeader>
                <CardTitle>Datos Bancarios</CardTitle>
                <CardDescription>
                  Cuenta para recibir pagos de clientes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>IBAN</Label>
                    <Input
                      value={settings.bank_iban || ''}
                      onChange={(e) => setSettings({ ...settings, bank_iban: e.target.value.toUpperCase().replace(/\s/g, '') })}
                      placeholder="ES00 0000 0000 0000 0000 0000"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre del Banco</Label>
                    <Input
                      value={settings.bank_name || ''}
                      onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                      placeholder="Ej: Banco Santander"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Este IBAN aparecera en las facturas para que los clientes realicen el pago
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoicing Settings */}
          <TabsContent value="invoicing" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Valores por defecto</CardTitle>
                <CardDescription>
                  Configuración predeterminada para nuevas facturas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Días de pago por defecto</Label>
                    <Input
                      type="number"
                      value={settings.default_payment_terms_days || 30}
                      onChange={(e) => setSettings({ ...settings, default_payment_terms_days: parseInt(e.target.value) || 30 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>IVA por defecto</Label>
                    <Select
                      value={settings.default_tax_rate_id || ''}
                      onValueChange={(v) => setSettings({ ...settings, default_tax_rate_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="21% (general)" />
                      </SelectTrigger>
                      <SelectContent>
                        {taxRates.filter(r => r.is_active).map((rate) => (
                          <SelectItem key={rate.id} value={rate.id}>
                            {rate.name} ({rate.rate}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Textos de factura</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Notas predeterminadas</Label>
                  <Textarea
                    value={settings.invoice_notes_template || ''}
                    onChange={(e) => setSettings({ ...settings, invoice_notes_template: e.target.value })}
                    placeholder="Texto que aparecerá en las facturas por defecto..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pie de factura</Label>
                  <Textarea
                    value={settings.invoice_footer || ''}
                    onChange={(e) => setSettings({ ...settings, invoice_footer: e.target.value })}
                    placeholder="Información legal, datos bancarios, etc."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* VeriFactu */}
          <TabsContent value="verifactu" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  VeriFactu - Facturación Electrónica
                </CardTitle>
                <CardDescription>
                  Configuración para envío de facturas a la Agencia Tributaria (RD 1007/2023)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="space-y-1">
                    <p className="font-medium">Activar VeriFactu</p>
                    <p className="text-sm text-muted-foreground">
                      Las facturas emitidas se enviarán automáticamente a la AEAT
                    </p>
                  </div>
                  <Switch
                    checked={settings.verifactu_enabled || false}
                    onCheckedChange={(v) => setSettings({ ...settings, verifactu_enabled: v })}
                  />
                </div>

                {settings.verifactu_enabled && (
                  <>
                    <Separator />

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Entorno</Label>
                        <Select
                          value={settings.verifactu_environment}
                          onValueChange={(v) => setSettings({ ...settings, verifactu_environment: v as any })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="test">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">Test</Badge>
                                Pruebas (no envía a AEAT real)
                              </div>
                            </SelectItem>
                            <SelectItem value="production">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-green-100 text-green-700">Producción</Badge>
                                Envío real a AEAT
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>ID Certificado digital</Label>
                          <Input
                            value={settings.verifactu_certificate_id || ''}
                            onChange={(e) => setSettings({ ...settings, verifactu_certificate_id: e.target.value })}
                            placeholder="Identificador del certificado"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>ID Software (NIF+Nombre)</Label>
                          <Input
                            value={settings.verifactu_software_id || ''}
                            onChange={(e) => setSettings({ ...settings, verifactu_software_id: e.target.value })}
                            placeholder="NIF desarrollador + código"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nombre del software</Label>
                          <Input
                            value={settings.verifactu_software_name || 'Athenea MES'}
                            onChange={(e) => setSettings({ ...settings, verifactu_software_name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Versión</Label>
                          <Input
                            value={settings.verifactu_software_version || '1.0.0'}
                            onChange={(e) => setSettings({ ...settings, verifactu_software_version: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">Pendiente de configuración</p>
                          <p className="text-sm text-amber-700 mt-1">
                            Para activar el envío a AEAT necesitas:
                          </p>
                          <ul className="text-sm text-amber-700 mt-2 list-disc list-inside space-y-1">
                            <li>Certificado digital de empresa válido</li>
                            <li>Alta en el sistema SII/VeriFactu de la AEAT</li>
                            <li>Configurar las credenciales de conexión</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {!settings.verifactu_enabled && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-800">VeriFactu desactivado</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Las facturas se generarán con hash de cadena y código QR,
                          pero no se enviarán automáticamente a la AEAT.
                          Podrás activarlo cuando tengas el certificado digital configurado.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Connection Status */}
            <Card>
              <CardHeader>
                <CardTitle>Estado de conexión</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted rounded">
                    <span>Conexión a AEAT</span>
                    {settings.verifactu_enabled ? (
                      <Badge className="bg-amber-100 text-amber-700">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Sin configurar
                      </Badge>
                    ) : (
                      <Badge variant="outline">Desactivado</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded">
                    <span>Certificado digital</span>
                    <Badge variant="outline">No cargado</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded">
                    <span>Última sincronización</span>
                    <span className="text-sm text-muted-foreground">Nunca</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
